import React from "react";
import cloudImage from "../Images/cloud-ai.webp";
import { KeyRound, PlugZap, RadioTower, Cloud, Layers, GitBranch } from "lucide-react";
import SectionHeading from "./SectionHeading";

const cloudHighlights = [
  {
    icon: <Cloud size={20} />,
    title: "Cloud Infrastructure",
    description: "Leverage AWS, Google Cloud, and Azure for auto-scaling server environments, lowering infrastructure overhead.",
  },
  {
    icon: <Layers size={20} />,
    title: "Microservices Architecture",
    description: "Modular, independently deployable services that improve system maintainability and isolated fault tolerance.",
  },
  {
    icon: <GitBranch size={20} />,
    title: "Automated CI/CD Pipelines",
    description: "Streamlined deployment pipelines ensuring rapid, safe, and zero-downtime continuous code integration.",
  },
];

const advancedCapabilities = [
  {
    icon: <KeyRound size={20} />,
    title: "Authentication & Security Control",
    description:
      "Enterprise RBAC, OAuth2/SSO integrations, and multi-tenant permission layers for absolute data governance.",
    color: "text-[#355dff]",
    bg: "bg-[#F3F0FF]",
  },
  {
    icon: <PlugZap size={20} />,
    title: "Third-Party Integrations",
    description:
      "Seamless connectivity with Stripe, PayPal, CRMs, ERPs, and custom enterprise tools via reliable REST/GraphQL APIs.",
    color: "text-[#8B5CF6]",
    bg: "bg-[#F5F3FF]",
  },
  {
    icon: <RadioTower size={20} />,
    title: "Real-Time Data Processing",
    description:
      "Bi-directional WebSockets and event-driven pipelines providing instantaneous updates, live chat, and streaming metrics.",
    color: "text-[#06B6D4]",
    bg: "bg-[#ECFEFF]",
  },
];

export default function WebServiceCloudAndIntegrations() {
  return (
    <section className="bg-[#F8F8FB] py-20 px-6 border-t border-gray-200/60">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mx-auto">
          <SectionHeading
            eyebrow="Cloud & Architecture"
            title="Cloud Integration & Advanced Systems"
            description="Modern web products require cloud elasticity, seamless third-party connections, and instant real-time data streaming."
            theme="light"
            titleClassName="text-[#111827]"
            descriptionClassName="text-[16px] leading-8"
          />
        </div>

        {/* Top Split: Cloud & Microservices */}
        <div className="grid lg:grid-cols-2 gap-10 items-center mt-16">
          {/* Cloud Image Card */}
          <div className="relative group">
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-600 opacity-20 blur-xl group-hover:opacity-30 transition duration-500" />
            <div className="relative">
              <img
                src={cloudImage}
                alt="Cloud Integration & Infrastructure"
                className="w-full h-[360px] object-cover rounded-3xl shadow-lg border border-white/40"
              />
              <div className="absolute bottom-5 left-5 flex flex-wrap gap-2.5">
                {["AWS", "Google Cloud", "Azure", "Docker"].map((item) => (
                  <span
                    key={item}
                    className="bg-white/95 backdrop-blur-md px-4 py-2 rounded-full text-xs font-bold text-[#355dff] shadow-md border border-gray-100"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Cloud Highlights List */}
          <div className="space-y-4">
            {cloudHighlights.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-[#ECECEC] p-6 flex items-start gap-4 hover:shadow-lg transition duration-300 hover:-translate-y-1"
              >
                <div className="w-10 h-10 rounded-xl bg-[#EBF2FF] text-[#355dff] flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-gray-500 leading-6 text-[14px]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Cards: Advanced Backend Capabilities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          {advancedCapabilities.map((item, index) => (
            <div
              key={index}
              className="bg-white border border-[#ECECEC] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
            >
              <div
                className={`w-12 h-12 rounded-xl ${item.bg} flex items-center justify-center ${item.color} mb-6`}
              >
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-[#111827] leading-7 mb-3">
                {item.title}
              </h3>
              <p className="text-[14px] leading-7 text-gray-500">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
